import { type VisualizerData } from './visualizer';

export type EmojiId = string;
export type EmojiDescription = string;
export type EmojiFeature = string;
export type EmojiRhythmScore = number;
export type EmojiGeneration = number;

export enum EmojiIdAvailability {
    Available = 'Available',
    Taken = 'Taken',
    ComingSoon = 'ComingSoon',
    // InCart = 'InCart',
    // NoPrice = 'NoPrice'
}

export interface EmojiCopy {
    description: EmojiDescription;
    features: EmojiFeature[];
}

export interface EmojiIdSearchResult {
    emoji_id: EmojiId;
    available: boolean;
    availability: EmojiIdAvailability;
    copy: EmojiCopy;
    price: number;
    length: number;
    views_past_month: number;
    rhythm_score: EmojiRhythmScore;
    generation: EmojiGeneration;
}

export interface RandomResponse {
    result: EmojiIdSearchResult[];
}

export interface SearchResponse {
    result: EmojiIdSearchResult;
    alternates: EmojiIdSearchResult[];
}

export interface RhythmResponse {
    rhythm: number;
}

export interface SignatureResponse {
    account: string;
    expiry: number;
    signature: string;
    token: EmojiId;
}

export interface TagQuery {
    tags?: string;
}

export interface TagResponse {
    status: boolean;
    result?: Tag[];
    error?: TagError;
}

export interface Tag {
    tag: EidCategory;
    data: EidCategoryItem;
    hash: EidCategoryDataHash;
}

export interface TagError {
    code: EmojiErrorCode;
    reason: string;
}

export interface UpdateTagsRequest {
    insert?: EidCategories[];
    delete?: EidCategoryDataHash[];
    merkle_root?: string;
    signature?: string;
}

export enum EmojiPowerUp {
    YatPage = 'YatPage',
    Redirect = 'Redirect',
}

export enum EidCategory {
    BTC_ADDRESS = '0x1003',
    DNS_A = '0x4101',
    DNS_AAAA = '0x411C',
    DNS_CNAME = '0x4105',
    DNS_LOC = '0x411D',
    DNS_MX = '0x410F',
    DNS_NS = '0x4102',
    DNS_OPENPGPKEY = '0x413D',
    DNS_TXT = '0x4110',
    EMOJI_DESCRIPTION = '0x0002',
    EMOJI_MODIFIERS = '0x0001',
    EMOJI_PREFERRED = '0x0006',
    EMOJI_NICK = '0x0005',
    EMOJI_PUBKEY = '0x0000',
    ETH_ADDRESS = '0x1004',
    EXPIRES_AT = '0x0003',
    LTC_ADDRESS = '0x1019',
    MONERO_STD_ADDRESS = '0x1001',
    MONERO_SUB_ADDRESS = '0x1002',
    ONION3ADDRESS = '0x4000',
    PAYMENT_ADDRESS = '0x6200',
    REDIRECT = '0x0004',
    SOCIAL_MEDIA_HANDLE = '0x6000',
    TARI_PUBKEY = '0x0101',
    XTR = '0x0103',
    TARI_SCHNORR_SIGNATURE = '0x0102',
    UNSPECIFIED_CRYPTO = '0x3fff',
    URL = '0x4001',
    DELIMITED_WEB_URL = '0x4002',
}

export type EidCategoryValidator = {
    [Key in EidCategory]: (eidItem: EidCategoryItem) => boolean;
};
const hostnameRegex =
    /^(([a-zA-Z0-9_]|[a-zA-Z0-9_][a-zA-Z0-9_-]*[a-zA-Z0-9_])\.)*([A-Za-z0-9_]|[A-Za-z0-9_][A-Za-z0-9_-]*[A-Za-z0-9_](\.?))$/;
const urlRegex =
    /^(http[s]?:\/\/){1}(([a-zA-Z0-9_]|[a-zA-Z0-9_][a-zA-Z0-9_-]*[a-zA-Z0-9_])\.)*([A-Za-z0-9_]|[A-Za-z0-9_][A-Za-z0-9_-]*[A-Za-z0-9_](\.?))$/;
const mxRegex =
    /^[0-9]+\s+(([a-zA-Z0-9_]|[a-zA-Z0-9_][a-zA-Z0-9_-]*[a-zA-Z0-9_])\.)*([A-Za-z0-9_]|[A-Za-z0-9_][A-Za-z0-9_-]*[A-Za-z0-9_](\.?))$/;
const ipv4Regex =
    /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
const ipv6Regex =
    /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;

// @TODO This is not the right way to do it, the checksums need to be checked. Investigate https://www.npmjs.com/package/bitcoin-address-validation
const bitcoinRegex = /^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/;

export const eidValidators: EidCategoryValidator = {
    [EidCategory.EMOJI_PUBKEY]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.UNSPECIFIED_CRYPTO]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.URL]: (eidItem: EidCategoryItem) => urlRegex.test(eidItem),
    [EidCategory.REDIRECT]: (eidItem: EidCategoryItem) => urlRegex.test(eidItem),
    [EidCategory.TARI_SCHNORR_SIGNATURE]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.EXPIRES_AT]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.BTC_ADDRESS]: (eidItem: EidCategoryItem) => bitcoinRegex.test(eidItem),
    [EidCategory.ETH_ADDRESS]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.LTC_ADDRESS]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.TARI_PUBKEY]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.XTR]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.MONERO_STD_ADDRESS]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.MONERO_SUB_ADDRESS]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.ONION3ADDRESS]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.DNS_A]: (eidItem: EidCategoryItem) => ipv4Regex.test(eidItem),
    [EidCategory.DNS_AAAA]: (eidItem: EidCategoryItem) => ipv6Regex.test(eidItem),
    [EidCategory.DNS_CNAME]: (eidItem: EidCategoryItem) => hostnameRegex.test(eidItem),
    [EidCategory.DNS_LOC]: (_eidItem: EidCategoryItem) => true,
    [EidCategory.DNS_MX]: (eidItem: EidCategoryItem) => mxRegex.test(eidItem),
    [EidCategory.DNS_NS]: (eidItem: EidCategoryItem) => hostnameRegex.test(eidItem),
    [EidCategory.DNS_OPENPGPKEY]: (eidItem: EidCategoryItem) => !!eidItem.length,
    [EidCategory.DNS_TXT]: (eidItem: EidCategoryItem) => !!eidItem.length,
    [EidCategory.EMOJI_DESCRIPTION]: (eidItem: EidCategoryItem) => !!eidItem.length,
    [EidCategory.EMOJI_MODIFIERS]: (eidItem: EidCategoryItem) => !!eidItem.length,
    [EidCategory.EMOJI_PREFERRED]: (eidItem: EidCategoryItem) => !!eidItem.length,
    [EidCategory.EMOJI_NICK]: (eidItem: EidCategoryItem) => !!eidItem.length,
    [EidCategory.PAYMENT_ADDRESS]: (eidItem: EidCategoryItem) => !!eidItem.length,
    [EidCategory.SOCIAL_MEDIA_HANDLE]: (eidItem: EidCategoryItem) => !!eidItem.length,
    [EidCategory.DELIMITED_WEB_URL]: (eidItem: EidCategoryItem) => !!eidItem.length,
};

export enum EmojiErrorCode {
    /// Emoji Id already registered
    ALREADY_REGISTERED = 405,
    /// Internal error from DB subsystem
    INTERNAL_DB = 502,
    /// Invalid category = 4
    INVALID_CATEGORY = 4,
    /// Invalid data, usually unparseable hex = 6
    INVALID_DATA = 6,
    /// Invalid emoji ID = 1
    INVALID_ID = 1,
    /// Invalid emoji merkle root = 3
    INVALID_MERKLE_ROOT = 3,
    /// Invalid emoji public key = 5
    INVALID_PUBKEY = 5,
    /// Invalid emoji signature = 2
    INVALID_SIGNATURE = 2,
    /// Emoji Id not avilable for registration or changes
    NOT_AVAILABLE = 403,
    /// Emoji Id not found = 404
    NOT_FOUND = 404,
    /// No such category = 7
    NO_CATEGORY = 7,
    /// No such category item record = 8
    NO_RECORD = 8,
}

export type EidCategoryItem = string;
export type EidCategoryDataHash = string;
export type EidCategoryData = EidCategoryItem[];
export type EidCategories = {
    [Key in EidCategory]: EidCategoryData;
};

export type AccountAddress = string;
export type SignatureExpiry = number;

export type ArtifactSignatureParams = EmojiSignatureParams;
export interface EmojiSignatureParams {
    account: AccountAddress;
    expiry?: SignatureExpiry;
}

export type EmojiVisualizerName = string;
export type EmojiVisualizerMetadata = VisualizerData;
export interface EmojiVisualizerFiles {
    image: string;
    video: string;
}
export type EmojiVisualizerIsLocked = boolean | null;
export type EmojiVisualizerNameIsLocked = boolean | null;

// function generateEidCategory() {
//     let str = 'export enum EidCategory {';
//     Object.keys(EidCategoryHex).forEach((k) => {
//         str += `${k} = '${parseInt(EidCategoryHex[k])}',`;
//     });
//     str += `}`;
//
// }
//
// generateEidCategory();

export type EmojiSinglePayment = {
    address: string;
    category: string;
    default: boolean;
    description: string;
    long_name: string;
    settlement_network: string;
    short_name: string;
    signature: string;
    hash: string;
};

export interface SearchQuery {
    emoji_id: EmojiId;
}

export enum EmojiShapes {
    Adoptables = 'Adoptables',
    Bookends = 'Bookends',
    Repeaters = 'Repeaters',
}

export interface EmojiShape {
    shape: EmojiShapes[];
    pattern: string | null;
}

export interface ExtendedEmoji {
    canonical_format: EmojiId;
    blocked_until: string;
    display_format: EmojiId;
    chain_format: EmojiId;
    flippable_emoji: boolean[];
    free: boolean;
    generation: number;
    minted: boolean;
    powerup?: EmojiPowerUp;
    rhythm_score?: number;
    shape: EmojiShape;
    shortname: string;
    token_id: number;
}

export type EmojiPaymentResponse = {
    status: boolean;
    result: Record<string, EmojiSinglePayment>;
    error: string;
};

export type EmojiTag = {
    data: string;
    tag: string;
};

export interface EmojiAddPaymentRequest {
    delete: string[];
    insert: EmojiTag[];
}

export type RecentEmojiReponse = string[];
